/**
 * Matter context panel.
 *
 * Displays matter reference, claimant, client, partner, and phase information.
 * Supports both direct ID lookup and text search against the gateway.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Text,
  Badge,
  Spinner,
  Input,
  Button,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Building20Regular } from '@fluentui/react-icons/svg/building';
import { CheckmarkCircle20Regular } from '@fluentui/react-icons/svg/checkmark-circle';
import { Dismiss20Regular } from '@fluentui/react-icons/svg/dismiss';
import { DocumentLink20Regular } from '@fluentui/react-icons/svg/document-link';
import { Link20Regular } from '@fluentui/react-icons/svg/link';
import { Person20Regular } from '@fluentui/react-icons/svg/person';
import { Search20Regular } from '@fluentui/react-icons/svg/search';
import { Warning20Regular } from '@fluentui/react-icons/svg/warning';
import { useGateway } from '../../api/useGateway';
import { useAuth } from '../../auth/auth-context';
import type { MatterSummary, ReportingSchema } from '../../api/gateway-client';
import type { DocumentLink } from '@alterspective/shared';
import { writeMatterLink, detectWordOnline } from '../../office/document-metadata';

interface MatterPanelProps {
  matterId: string | null;
  onMatterIdChange: (id: string | null) => void;
  existingLink?: DocumentLink | null;
  onLinkChange?: (link: DocumentLink) => void;
}

const BRAND_COLOR = '#075156';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },

  // ── Link banner (top) ────────────────────────────────────────────────
  linkBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: BRAND_COLOR,
    color: '#fff',
  },
  linkBannerIcon: {
    flexShrink: 0,
    color: '#fff',
  },
  linkBannerText: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#fff',
    flex: 1,
  },
  linkBannerSub: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.75)',
  },
  linkBannerRelinkBtn: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: '11px',
    padding: '2px 6px',
    minWidth: 'unset',
    height: '22px',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: '4px',
    cursor: 'pointer',
    background: 'transparent',
    flexShrink: 0,
  },

  // ── Search bar area ──────────────────────────────────────────────────
  searchArea: {
    padding: '12px 12px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  searchRow: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  searchInput: {
    flex: 1,
  },

  // ── Empty state ──────────────────────────────────────────────────────
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    gap: '10px',
    color: tokens.colorNeutralForeground3,
  },
  emptyIcon: {
    fontSize: '40px',
    opacity: 0.35,
    color: BRAND_COLOR,
  },
  emptyTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  emptyHint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
    maxWidth: '200px',
    lineHeight: '1.5',
  },
  emptyExamples: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: '4px',
  },
  exampleChip: {
    fontSize: '11px',
    padding: '3px 8px',
    borderRadius: '10px',
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  // ── Search results ───────────────────────────────────────────────────
  resultsWrap: {
    padding: '0 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  resultsCount: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
    padding: '0 2px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  resultCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '10px 12px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    transition: 'border-color 0.15s, background-color 0.15s',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  resultCardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  resultCardTitle: {
    fontSize: '13px',
    fontWeight: 600,
    flex: 1,
    color: tokens.colorNeutralForeground1,
  },
  resultCardRef: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  resultCardMeta: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  resultCardMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },

  // ── Matter detail card ───────────────────────────────────────────────
  matterCard: {
    padding: '0 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
  matterHeader: {
    padding: '12px 14px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '12px',
  },
  matterTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
  },
  matterRefRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  matterRef: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontFamily: 'monospace',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0px',
  },
  fieldCell: {
    padding: '8px 6px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  fieldLabel: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: tokens.colorNeutralForeground3,
  },
  fieldValue: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground1,
    fontWeight: 500,
  },
  fieldValueMuted: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
  matterActions: {
    display: 'flex',
    gap: '6px',
    paddingTop: '10px',
  },
});

function getClientLabel(schema: ReportingSchema | undefined, path: string, defaultLabel: string): string {
  if (!schema) return defaultLabel;
  for (const reportType of Object.values(schema.reportTypes)) {
    const field = reportType.fields.find((f) => f.path === path);
    if (field) return field.label;
  }
  return defaultLabel;
}

function looksLikeId(input: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input)
    || /^wi-\d+$/i.test(input);
}

export const MatterPanel: React.FC<MatterPanelProps> = ({
  matterId,
  onMatterIdChange,
  existingLink,
  onLinkChange,
}) => {
  const styles = useStyles();
  const gateway = useGateway();
  const auth = useAuth();
  const [inputValue, setInputValue] = useState(matterId ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matter, setMatter] = useState<MatterSummary | null>(null);
  const [searchResults, setSearchResults] = useState<MatterSummary[] | null>(null);
  const [pendingRelinkResult, setPendingRelinkResult] = useState<MatterSummary | null>(null);
  const requestIdRef = useRef(0);
  const isWordOnline = detectWordOnline();

  const loadMatter = useCallback((id: string) => {
    if (!gateway || !id.trim()) return;
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    setSearchResults(null);

    gateway.getMatter(id.trim())
      .then((result) => {
        if (requestId !== requestIdRef.current) return;
        setMatter(result);
        onMatterIdChange(result.id);
      })
      .catch((err: unknown) => {
        if (requestId !== requestIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load matter');
        setMatter(null);
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setIsLoading(false);
      });
  }, [gateway, onMatterIdChange]);

  const searchMatters = useCallback((query: string) => {
    if (!gateway || !query.trim()) return;
    const requestId = ++requestIdRef.current;
    setIsSearching(true);
    setError(null);
    setMatter(null); // clear prior matter so results list renders

    gateway.searchMatters(query.trim(), 10)
      .then((result) => {
        if (requestId !== requestIdRef.current) return;
        setSearchResults(result.results);
        if (result.results.length === 0) {
          setError('No matters found matching your search.');
        }
      })
      .catch((err: unknown) => {
        if (requestId !== requestIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Search failed');
        setSearchResults(null);
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setIsSearching(false);
      });
  }, [gateway]);

  useEffect(() => {
    if (matterId && gateway) {
      loadMatter(matterId);
    }
  }, [matterId, gateway]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    const query = inputValue.trim();
    if (!query) return;
    if (looksLikeId(query)) {
      loadMatter(query);
    } else {
      searchMatters(query);
    }
  };

  const confirmLink = useCallback(async (result: MatterSummary, source: DocumentLink['linkSource']) => {
    setIsLinking(true);
    setError(null);

    const link: DocumentLink = {
      matterId: result.id,
      matterReference: result.reference,
      linkedAt: new Date().toISOString(),
      linkedBy: auth?.user?.email ?? auth?.user?.displayName ?? 'unknown',
      linkSource: source,
    };

    try {
      await writeMatterLink(link);
      if (gateway) {
        await gateway.linkDocument(result.id, {
          matterReference: link.matterReference,
          linkedAt: link.linkedAt,
          linkedBy: link.linkedBy,
          linkSource: link.linkSource,
        }).catch(() => {});
      }
      onLinkChange?.(link);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save matter link');
    } finally {
      setIsLinking(false);
    }

    setMatter(result);
    setSearchResults(null);
    setInputValue(result.reference || result.id);
    onMatterIdChange(result.id);
  }, [auth, gateway, onLinkChange, onMatterIdChange]);

  const handleSelectResult = (result: MatterSummary) => {
    if (existingLink && existingLink.matterId !== result.id) {
      setPendingRelinkResult(result);
      return;
    }
    void confirmLink(result, 'manual');
  };

  const handleConfirmRelink = () => {
    if (!pendingRelinkResult) return;
    setPendingRelinkResult(null);
    void confirmLink(pendingRelinkResult, 're-linked');
  };

  const handleClear = () => {
    setMatter(null);
    setSearchResults(null);
    setError(null);
    setInputValue('');
    onMatterIdChange(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
  };

  if (!gateway) {
    return (
      <div className={styles.emptyState}>
        <DocumentLink20Regular className={styles.emptyIcon} />
        <span className={styles.emptyTitle}>Not connected</span>
        <span className={styles.emptyHint}>Sign in to search for matters.</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>

      {/* ── Document link banner ──────────────────────────────────── */}
      {existingLink && (
        <div className={styles.linkBanner}>
          <CheckmarkCircle20Regular className={styles.linkBannerIcon} />
          <div style={{ flex: 1 }}>
            <div className={styles.linkBannerText}>Linked — {existingLink.matterReference}</div>
            <div className={styles.linkBannerSub}>
              {existingLink.linkedBy
                ? `by ${existingLink.linkedBy.split('@')[0]}`
                : 'Document is linked to this matter'}
            </div>
          </div>
          <button
            className={styles.linkBannerRelinkBtn}
            onClick={handleClear}
            title="Search for a different matter"
          >
            Change
          </button>
        </div>
      )}

      {/* ── Word Online warning ───────────────────────────────────── */}
      {isWordOnline && (
        <MessageBar intent="warning" style={{ margin: '8px 12px 0' }}>
          <MessageBarBody>
            <Warning20Regular style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Word Online may not persist the matter link. Save as .docx to preserve it.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* ── Re-link confirmation ──────────────────────────────────── */}
      {pendingRelinkResult && (
        <Dialog open onOpenChange={() => setPendingRelinkResult(null)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Re-link document?</DialogTitle>
              <DialogContent>
                This document is already linked to <strong>{existingLink?.matterReference}</strong>.
                Switch to <strong>{pendingRelinkResult.reference}</strong>?
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setPendingRelinkResult(null)}>Cancel</Button>
                <Button appearance="primary" onClick={handleConfirmRelink}
                  style={{ backgroundColor: BRAND_COLOR }}>
                  Re-link
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {/* ── Search area ───────────────────────────────────────────── */}
      <div className={styles.searchArea}>
        <div className={styles.searchRow}>
          <Input
            className={styles.searchInput}
            placeholder="Search by name, ref, or ID…"
            value={inputValue}
            onChange={(_e, data) => setInputValue(data.value)}
            onKeyDown={handleKeyDown}
            size="medium"
            contentBefore={<Search20Regular style={{ color: tokens.colorNeutralForeground3 }} />}
          />
          <Button
            appearance="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={isLoading || isSearching || !inputValue.trim()}
            style={{ backgroundColor: BRAND_COLOR, minWidth: 72 }}
          >
            {(isLoading || isSearching) ? <Spinner size="tiny" /> : 'Search'}
          </Button>
          {matter && (
            <Button
              appearance="subtle"
              icon={<Dismiss20Regular />}
              size="medium"
              onClick={handleClear}
              title="Clear matter"
            />
          )}
        </div>

        {isLinking && (
          <Spinner size="tiny" label="Saving link…" style={{ alignSelf: 'flex-start' }} />
        )}

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
      </div>

      {/* ── Search results ────────────────────────────────────────── */}
      {searchResults && searchResults.length > 0 && !matter && (
        <div className={styles.resultsWrap}>
          <span className={styles.resultsCount}>
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
          </span>
          {searchResults.map((result) => (
            <div
              key={result.id}
              className={styles.resultCard}
              onClick={() => handleSelectResult(result)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSelectResult(result); }}
            >
              <div className={styles.resultCardTop}>
                <span className={styles.resultCardTitle}>{result.title}</span>
                <Badge
                  appearance="filled"
                  color={result.status === 'active' ? 'success' : 'warning'}
                  size="small"
                >
                  {result.status}
                </Badge>
              </div>
              <span className={styles.resultCardRef}>{result.reference}</span>
              <div className={styles.resultCardMeta}>
                {result.client?.name && (
                  <span className={styles.resultCardMetaItem}>
                    <Building20Regular style={{ fontSize: 12 }} />
                    {result.client.name}
                  </span>
                )}
                {result.claimant?.name && (
                  <span className={styles.resultCardMetaItem}>
                    <Person20Regular style={{ fontSize: 12 }} />
                    {result.claimant.name}
                  </span>
                )}
                {result.phase && (
                  <span className={styles.resultCardMetaItem}>
                    {result.phase}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state (no matter, no results) ──────────────────── */}
      {!isLoading && !isSearching && !matter && !searchResults && !error && (
        <div className={styles.emptyState}>
          <Search20Regular className={styles.emptyIcon} />
          <span className={styles.emptyTitle}>Find a matter</span>
          <span className={styles.emptyHint}>
            Search by matter name, reference number, or paste a matter ID.
          </span>
          <div className={styles.emptyExamples}>
            {['Smith v Insurance Corp', 'REF-2026-001', 'wi-001'].map((ex) => (
              <span
                key={ex}
                className={styles.exampleChip}
                onClick={() => { setInputValue(ex); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') { setInputValue(ex); } }}
              >
                {ex}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Matter detail ─────────────────────────────────────────── */}
      {matter && !isLoading && (
        <div className={styles.matterCard}>
          {/* Header card */}
          <div className={styles.matterHeader}>
            <span className={styles.matterTitle}>{matter.title}</span>
            <div className={styles.matterRefRow}>
              <span className={styles.matterRef}>{matter.reference}</span>
              <Badge
                appearance="filled"
                color={matter.status === 'active' ? 'success' : 'warning'}
                size="small"
              >
                {matter.status}
              </Badge>
              {matter.phase && (
                <Badge appearance="outline" color="informative" size="small">
                  {matter.phase}
                </Badge>
              )}
            </div>
          </div>

          {/* Fields grid */}
          <div className={styles.fieldsGrid}>
            {[
              [getClientLabel(matter.reportingSchema, 'claimant', 'Claimant'), matter.claimant?.name],
              [getClientLabel(matter.reportingSchema, 'client', 'Instructing Client'), matter.client?.name],
              [getClientLabel(matter.reportingSchema, 'defendant', 'Defendant'), matter.defendant],
              [getClientLabel(matter.reportingSchema, 'partner', 'Responsible Partner'), matter.partner?.name],
              ...(matter.claimReference ? [[getClientLabel(matter.reportingSchema, 'claim-reference', 'Insurer Ref'), matter.claimReference]] : []),
              ...(matter.incidentDate ? [[getClientLabel(matter.reportingSchema, 'incident-date', 'Incident Date'), new Date(matter.incidentDate).toLocaleDateString('en-AU')]] : []),
            ].map(([label, value]) => (
              <div key={label as string} className={styles.fieldCell}>
                <span className={styles.fieldLabel}>{label as string}</span>
                {value
                  ? <span className={styles.fieldValue}>{value as string}</span>
                  : <span className={styles.fieldValueMuted}>—</span>
                }
              </div>
            ))}
          </div>

          {/* Actions */}
          {!existingLink && (
            <div className={styles.matterActions}>
              <Button
                appearance="primary"
                size="small"
                icon={<Link20Regular />}
                onClick={() => void confirmLink(matter, 'manual')}
                disabled={isLinking}
                style={{ backgroundColor: BRAND_COLOR }}
              >
                Link this document
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
